import {
  collection,
  doc,
  addDoc,
  getDoc,
  getDocs,
  updateDoc,
  onSnapshot,
  query,
  where,
  serverTimestamp,
  orderBy,
  setDoc,
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { httpsCallable } from 'firebase/functions';
import { firestore, storage, functions, auth } from '../config/firebase';
import { queueInvitationEmail } from './invitationEmailService';
import {
  ensureInitialFundInvestorTranche,
  addManualFundInvestorCapitalTranche,
  upsertFundInvestorCapitalTranche,
} from './fundInvestorCapitalTranchesService';

const COLLECTION = 'fundInvestors';

const summarizeFundInvestorContractCallable = httpsCallable(functions, 'summarizeFundInvestorContract');
const CONTRACT_PREFIX = 'fundInvestors';
const CHEQUE_SUBFOLDER = 'chequeSpecimen';
const INVESTOR_SIGNED_CONTRACT_SUBFOLDER = 'signedByInvestor';
const FULLY_EXECUTED_CONTRACT_SUBFOLDER = 'fullyExecuted';
const QUALIFICATION_DOCS_SUBFOLDER = 'qualificationDocs';
const INVITE_TOKENS = 'inviteTokens';

/** Bump when legal wording changes (stored on each attestation). */
export const QUALIFIED_INVESTOR_ATTESTATION_VERSION = '1';

/**
 * Investor-side "qualified investor" step is satisfied if they self-attested in the app, or the placement is
 * already active in the fund (funds received). The latter matches managers who onboarded investors outside the
 * self-attestation flow while still using the same fundInvestor document.
 * @param {object | null | undefined} fi fundInvestor doc (client shape)
 * @returns {boolean}
 */
export function isFundInvestorAttestationSatisfied(fi) {
  if (!fi) return false;
  if (fi.qualifiedInvestorAttestedAt != null) return true;
  if (fi.status === 'funds_received') return true;
  return false;
}

const QUALIFICATION_DOCS_TEMPLATE_BY_CATEGORY = {
  accredited: [
    { key: 'proof_income', labelKey: 'fundInvestors.qualificationDocs.itemProofIncome', requireFile: true },
    { key: 'proof_net_worth', labelKey: 'fundInvestors.qualificationDocs.itemProofNetWorth', requireFile: true },
  ],
  eligible: [
    { key: 'proof_income', labelKey: 'fundInvestors.qualificationDocs.itemProofIncome', requireFile: true },
    { key: 'proof_eligible', labelKey: 'fundInvestors.qualificationDocs.itemProofEligible', requireFile: true },
  ],
  institution: [
    { key: 'institution_docs', labelKey: 'fundInvestors.qualificationDocs.itemInstitutionDocs', requireFile: true },
    { key: 'authorization_docs', labelKey: 'fundInvestors.qualificationDocs.itemAuthorizationDocs', requireFile: true },
  ],
};

/**
 * @param {'accredited'|'eligible'|'institution'|string} category
 * @returns {Array<{ key: string, labelKey: string, requireFile: boolean }>}
 */
export function getQualificationDocsTemplate(category) {
  if (!category) return QUALIFICATION_DOCS_TEMPLATE_BY_CATEGORY.accredited;
  const normalized = String(category);
  return QUALIFICATION_DOCS_TEMPLATE_BY_CATEGORY[normalized] || QUALIFICATION_DOCS_TEMPLATE_BY_CATEGORY.accredited;
}

function sanitizeStorageKey(s) {
  return String(s).trim().replace(/[^a-zA-Z0-9_-]/g, '_');
}

/**
 * Upload a file in the qualification docs area for a fund investor.
 * Stored under: fundInvestors/{fundInvestorId}/qualificationDocs/{docKey}/{file}
 */
export async function uploadQualificationDocFile(fundInvestorId, docKey, file) {
  if (!fundInvestorId || !docKey || !file) throw new Error('fundInvestorId, docKey and file required');
  const fileId = `${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
  const safeKey = sanitizeStorageKey(docKey);
  const safeName = (file.name || 'document').replace(/[^a-zA-Z0-9._-]/g, '_');
  const path = `${CONTRACT_PREFIX}/${fundInvestorId}/${QUALIFICATION_DOCS_SUBFOLDER}/${safeKey}/${fileId}_${safeName}`;
  const storageRefPath = ref(storage, path);
  await uploadBytes(storageRefPath, file);

  const fileRef = {
    storagePath: path,
    name: file.name || safeName,
    size: file.size,
    uploadedAt: new Date().toISOString(),
  };

  // Persist on fundInvestor so the checklist UI and submit validation see the upload (Storage alone is not enough).
  const refDoc = doc(firestore, COLLECTION, fundInvestorId);
  await updateDoc(refDoc, {
    [`qualificationDocsUploads.${safeKey}`]: fileRef,
    updatedAt: serverTimestamp(),
  });

  return fileRef;
}

export async function requestQualificationDocs(fundInvestorId, category) {
  if (!fundInvestorId) throw new Error('fundInvestorId required');
  const valid = ['accredited', 'eligible', 'institution'];
  const normalized = valid.includes(category) ? category : 'accredited';

  const refDoc = doc(firestore, COLLECTION, fundInvestorId);
  await updateDoc(refDoc, {
    qualificationDocsRequestStatus: 'requested',
    qualificationDocsRequestedAt: serverTimestamp(),
    qualificationDocsCategory: normalized,
    qualificationDocsUploads: {},
    qualificationDocsSubmittedAt: null,
    qualificationDocsReviewedAt: null,
    updatedAt: serverTimestamp(),
  });
}

export async function submitQualificationDocs(fundInvestorId) {
  if (!fundInvestorId) throw new Error('fundInvestorId required');
  await updateFundInvestor(fundInvestorId, {
    qualificationDocsRequestStatus: 'submitted',
    qualificationDocsSubmittedAt: serverTimestamp(),
  });
}

export async function reviewQualificationDocs(fundInvestorId) {
  if (!fundInvestorId) throw new Error('fundInvestorId required');
  await updateFundInvestor(fundInvestorId, {
    qualificationDocsRequestStatus: 'reviewed',
    qualificationDocsReviewedAt: serverTimestamp(),
  });
}

function generateInvitationToken() {
  // Même logique que pour les clients : token opaque difficile à deviner
  return crypto.randomUUID().replace(/-/g, '') + Date.now().toString(36);
}

/**
 * Upload contract PDF for a fund investor. Returns fileRef to store.
 * @param {string} fundInvestorId
 * @param {File} file
 * @returns {Promise<{ storagePath: string, name: string, size?: number, uploadedAt: string }>}
 */
export async function uploadFundInvestorContract(fundInvestorId, file) {
  if (!fundInvestorId || !file) throw new Error('fundInvestorId and file required');
  const fileId = `${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
  const safeName = (file.name || 'contract.pdf').replace(/[^a-zA-Z0-9._-]/g, '_');
  const path = `${CONTRACT_PREFIX}/${fundInvestorId}/${fileId}_${safeName}`;
  const storageRefPath = ref(storage, path);
  await uploadBytes(storageRefPath, file);
  return {
    storagePath: path,
    name: file.name || safeName,
    size: file.size,
    uploadedAt: new Date().toISOString(),
  };
}

/**
 * Upload cheque specimen (image or PDF) for interest deposits.
 * @param {string} fundInvestorId
 * @param {File} file
 * @returns {Promise<{ storagePath: string, name: string, size?: number, uploadedAt: string }>}
 */
export async function uploadFundInvestorChequeSpecimen(fundInvestorId, file) {
  if (!fundInvestorId || !file) throw new Error('fundInvestorId and file required');
  const fileId = `${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
  const safeName = (file.name || 'cheque').replace(/[^a-zA-Z0-9._-]/g, '_');
  const path = `${CONTRACT_PREFIX}/${fundInvestorId}/${CHEQUE_SUBFOLDER}/${fileId}_${safeName}`;
  const storageRefPath = ref(storage, path);
  await uploadBytes(storageRefPath, file);
  return {
    storagePath: path,
    name: file.name || safeName,
    size: file.size,
    uploadedAt: new Date().toISOString(),
  };
}

/**
 * @param {string} storagePath
 * @returns {Promise<string>}
 */
export async function getFundInvestorFileDownloadUrl(storagePath) {
  if (!storagePath) throw new Error('storagePath required');
  return getDownloadURL(ref(storage, storagePath));
}

/**
 * Investor: upload or replace cheque specimen; flags lender notice.
 * @param {string} fundInvestorId
 * @param {File} file
 */
export async function setFundInvestorChequeSpecimen(fundInvestorId, file) {
  const chequeSpecimenFileRef = await uploadFundInvestorChequeSpecimen(fundInvestorId, file);
  await updateDoc(doc(firestore, COLLECTION, fundInvestorId), {
    chequeSpecimenFileRef,
    chequeSpecimenUpdatedAt: serverTimestamp(),
    chequeSpecimenPendingLenderNotice: true,
    updatedAt: serverTimestamp(),
  });
}

/**
 * @param {string} fundInvestorId
 * @param {File} file
 * @returns {Promise<{ storagePath: string, name: string, size?: number, uploadedAt: string }>}
 */
export async function uploadFundInvestorInvestorSignedContract(fundInvestorId, file) {
  if (!fundInvestorId || !file) throw new Error('fundInvestorId and file required');
  const fileId = `${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
  const safeName = (file.name || 'contract-signed.pdf').replace(/[^a-zA-Z0-9._-]/g, '_');
  const path = `${CONTRACT_PREFIX}/${fundInvestorId}/${INVESTOR_SIGNED_CONTRACT_SUBFOLDER}/${fileId}_${safeName}`;
  const storageRefPath = ref(storage, path);
  await uploadBytes(storageRefPath, file);
  return {
    storagePath: path,
    name: file.name || safeName,
    size: file.size,
    uploadedAt: new Date().toISOString(),
  };
}

/**
 * Investor: upload PDF of the contract signed by the investor (returned to the fund manager).
 */
export async function setFundInvestorInvestorSignedContract(fundInvestorId, file) {
  const uid = auth.currentUser?.uid;
  if (!uid) throw new Error('Not authenticated');
  const refDoc = doc(firestore, COLLECTION, fundInvestorId);
  const snap = await getDoc(refDoc);
  if (!snap.exists()) throw new Error('Placement not found');
  const d = snap.data();
  if (d.investorUserId !== uid) throw new Error('Not your placement');
  const investorSignedContractFileRef = await uploadFundInvestorInvestorSignedContract(fundInvestorId, file);
  await updateDoc(refDoc, {
    investorSignedContractFileRef,
    investorSignedContractUploadedAt: serverTimestamp(),
    investorSignedContractPendingLenderNotice: true,
    updatedAt: serverTimestamp(),
  });
}

/**
 * @param {string} fundInvestorId
 * @param {File} file
 */
export async function uploadFundInvestorFullyExecutedContract(fundInvestorId, file) {
  if (!fundInvestorId || !file) throw new Error('fundInvestorId and file required');
  const fileId = `${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
  const safeName = (file.name || 'contract-fully-executed.pdf').replace(/[^a-zA-Z0-9._-]/g, '_');
  const path = `${CONTRACT_PREFIX}/${fundInvestorId}/${FULLY_EXECUTED_CONTRACT_SUBFOLDER}/${fileId}_${safeName}`;
  const storageRefPath = ref(storage, path);
  await uploadBytes(storageRefPath, file);
  return {
    storagePath: path,
    name: file.name || safeName,
    size: file.size,
    uploadedAt: new Date().toISOString(),
  };
}

/**
 * Fund manager: upload the fully executed contract (both parties signed) for the investor folder.
 */
export async function setFundInvestorFullyExecutedContract(fundInvestorId, file) {
  const uid = auth.currentUser?.uid;
  if (!uid) throw new Error('Not authenticated');
  const refDoc = doc(firestore, COLLECTION, fundInvestorId);
  const snap = await getDoc(refDoc);
  if (!snap.exists()) throw new Error('Placement not found');
  if (snap.data().lenderUid !== uid) throw new Error('Not authorized');
  const fullyExecutedContractFileRef = await uploadFundInvestorFullyExecutedContract(fundInvestorId, file);
  const d = snap.data();
  const payload = {
    fullyExecutedContractFileRef,
    fullyExecutedContractUploadedAt: serverTimestamp(),
    investorSignedContractPendingLenderNotice: false,
    updatedAt: serverTimestamp(),
  };
  // Advance workflow: fully executed PDF ⇒ contract phase complete (same as "Mark contract signed").
  if (d.status === 'invited' || d.status === 'invitation_accepted') {
    payload.status = 'signed';
  }
  if (!d.contractSignedAt) {
    payload.contractSignedAt = serverTimestamp();
  }
  await updateDoc(refDoc, payload);
}

/**
 * Lender: mark cheque specimen as reviewed (clears in-app notice).
 * @param {string} fundInvestorId
 */
export async function acknowledgeFundInvestorChequeSpecimen(fundInvestorId) {
  await updateFundInvestor(fundInvestorId, {
    chequeSpecimenPendingLenderNotice: false,
  });
}

/**
 * Create a fund investor (invitation). Optionally upload contract PDF.
 * @param {{ lenderUid: string, email: string, investedAmount: number, interestStartDate: string, displayName?: string, investingEntityType?: 'individual' | 'company', investingCompanyId?: string | null, investingDisplayName?: string | null }} data
 * @param {File} [contractFile]
 * @returns {Promise<string>} fundInvestor doc id
 */
export async function createFundInvestor(data, contractFile = null) {
  const {
    lenderUid,
    email,
    investedAmount,
    interestStartDate,
    displayName,
    investingEntityType,
    investingCompanyId,
    investingDisplayName,
  } = data;
  if (!lenderUid || !email) throw new Error('lenderUid and email required');
  const amount = typeof investedAmount === 'number' && !Number.isNaN(investedAmount) ? investedAmount : 0;
  const startDate = interestStartDate || null;

  const entityType = investingEntityType === 'company' ? 'company' : 'individual';
  const docRef = await addDoc(collection(firestore, COLLECTION), {
    lenderUid,
    investorUserId: null,
    investedAmount: amount,
    interestStartDate: startDate,
    email: String(email).trim().toLowerCase(),
    displayName: displayName ? String(displayName).trim() : null,
    investingEntityType: entityType,
    investingCompanyId: investingCompanyId && String(investingCompanyId).trim() ? String(investingCompanyId).trim() : null,
    investingDisplayName: investingDisplayName && String(investingDisplayName).trim() ? String(investingDisplayName).trim() : null,
    createdAt: serverTimestamp(),
    createdByUid: lenderUid,
    updatedAt: serverTimestamp(),
    status: 'invited',
    contractFileRef: null,
    contractSignedAt: null,
    investorSignedContractFileRef: null,
    investorSignedContractUploadedAt: null,
    investorSignedContractPendingLenderNotice: false,
    fullyExecutedContractFileRef: null,
    fullyExecutedContractUploadedAt: null,
    fundsReceivedAt: null,
    chequeSpecimenFileRef: null,
    chequeSpecimenPendingLenderNotice: false,
  });

  if (contractFile) {
    const contractFileRef = await uploadFundInvestorContract(docRef.id, contractFile);
    await updateDoc(docRef, {
      contractFileRef,
      updatedAt: serverTimestamp(),
    });
  }
  if (amount > 0) {
    try {
      await ensureInitialFundInvestorTranche({
        fundInvestorId: docRef.id,
        lenderUid,
        investedAmount: amount,
        interestStartDate: startDate,
      });
    } catch (e) {
      console.error('[fundInvestors] ensureInitialFundInvestorTranche', e);
    }
  }
  return docRef.id;
}

/**
 * Import an already-active capital investor (onboarding treated as complete; tranches dated manually).
 * @param {{
 *   lenderUid: string,
 *   email: string,
 *   displayName?: string | null,
 *   tranches: Array<{ amount: number, startDate: string, note?: string | null }>,
 *   sendInvitation?: boolean,
 * }} data
 * @returns {Promise<string>} fundInvestor doc id
 */
export async function createFundInvestorLegacyImport(data) {
  const { lenderUid, email, displayName, tranches, sendInvitation } = data || {};
  if (!lenderUid || !email) throw new Error('lenderUid and email required');
  const rows = Array.isArray(tranches) ? tranches : [];
  let total = 0;
  const normalized = [];
  for (const t of rows) {
    const amt = Number(t?.amount);
    if (!Number.isFinite(amt) || amt <= 0) continue;
    const sd = t?.startDate && String(t.startDate).trim().slice(0, 10);
    if (!sd) throw new Error('Each tranche needs a valid startDate (YYYY-MM-DD)');
    normalized.push({
      amount: amt,
      startDate: sd,
      note: t?.note != null && String(t.note).trim() ? String(t.note).trim().slice(0, 500) : null,
    });
    total += amt;
  }
  if (normalized.length === 0 || total <= 0) {
    throw new Error('At least one tranche with a positive amount and start date is required');
  }
  const sortedByDate = [...normalized].sort((a, b) => a.startDate.localeCompare(b.startDate));
  const interestStartDate = sortedByDate[0].startDate;

  const docRef = await addDoc(collection(firestore, COLLECTION), {
    lenderUid,
    investorUserId: null,
    investedAmount: total,
    interestStartDate,
    email: String(email).trim().toLowerCase(),
    displayName: displayName ? String(displayName).trim() : null,
    investingEntityType: 'individual',
    investingCompanyId: null,
    investingDisplayName: null,
    createdAt: serverTimestamp(),
    createdByUid: lenderUid,
    updatedAt: serverTimestamp(),
    status: 'funds_received',
    legacyCapitalImport: true,
    /** Do not set fundsReceivedAt / contractSignedAt — they would show the import date, not real contract/deposit dates. */
    fundsReceivedAt: null,
    contractFileRef: null,
    contractSignedAt: null,
    investorSignedContractFileRef: null,
    investorSignedContractUploadedAt: null,
    investorSignedContractPendingLenderNotice: false,
    fullyExecutedContractFileRef: null,
    fullyExecutedContractUploadedAt: null,
    chequeSpecimenFileRef: null,
    chequeSpecimenPendingLenderNotice: false,
    qualifiedInvestorCategory: 'accredited',
    qualifiedInvestorAttestationVersion: QUALIFIED_INVESTOR_ATTESTATION_VERSION,
    qualifiedInvestorAttestedAt: serverTimestamp(),
    qualificationDocsRequestStatus: 'not_requested',
    qualificationDocsRequestedAt: null,
    qualificationDocsCategory: null,
    qualificationDocsUploads: {},
    qualificationDocsSubmittedAt: null,
    qualificationDocsReviewedAt: null,
  });

  for (let i = 0; i < sortedByDate.length; i += 1) {
    const t = sortedByDate[i];
    if (i === 0) {
      await upsertFundInvestorCapitalTranche({
        fundInvestorId: docRef.id,
        lenderUid,
        amount: t.amount,
        startDate: t.startDate,
        type: 'initial',
        sourceProposalId: null,
        sourceRepaymentId: null,
        note: t.note,
      });
    } else {
      await addManualFundInvestorCapitalTranche({
        fundInvestorId: docRef.id,
        lenderUid,
        amount: t.amount,
        startDate: t.startDate,
        note: t.note,
      });
    }
  }

  if (sendInvitation) {
    await createFundInvestorInvitation(
      lenderUid,
      docRef.id,
      String(email).trim().toLowerCase(),
      displayName ? String(displayName).trim() : null,
      { invitationScenario: 'migration' }
    );
  }

  return docRef.id;
}

/**
 * Create an invitation token + email for a fund investor.
 * @param {string} lenderUid
 * @param {string} fundInvestorId
 * @param {string} email
 * @param {string | null} displayName
 * @param {{ invitationScenario?: string }} [options]
 * @returns {Promise<{ invitationToken: string, invitationLink: string }>}
 */
export async function createFundInvestorInvitation(lenderUid, fundInvestorId, email, displayName, options = {}) {
  if (!lenderUid || !fundInvestorId || !email) {
    throw new Error('lenderUid, fundInvestorId and email required');
  }
  const invitationToken = generateInvitationToken();
  const inviteRef = doc(firestore, INVITE_TOKENS, invitationToken);
  await setDoc(inviteRef, {
    fundInvestorId,
    brokerUid: lenderUid,
    email: String(email).trim().toLowerCase(),
    investorName: displayName || '',
  });
  const base = typeof window !== 'undefined'
    ? `${window.location.origin}${window.location.pathname}#/signup?invitation=${invitationToken}`
    : '';
  const invitationScenario = typeof options?.invitationScenario === 'string' ? options.invitationScenario : null;
  if (base) {
    await queueInvitationEmail(lenderUid, {
      to: email,
      clientName: displayName || '',
      invitationLink: base,
      clientId: fundInvestorId,
      invitationKind: 'capital_investor',
      ...(invitationScenario ? { invitationScenario } : {}),
    });
  }
  return { invitationToken, invitationLink: base };
}

/**
 * Lier un utilisateur (investor) à un fundInvestor existant.
 * @param {string} fundInvestorId
 * @param {string} investorUserId
 */
/**
 * Mirror the investor's profile display name onto every fundInvestor row they are linked to,
 * so the lender's capital investors table stays in sync when the investor renames themselves.
 * @param {string} investorUserId
 * @param {string | null | undefined} displayName - empty after trim becomes null on documents
 */
export async function syncFundInvestorDisplayNameForInvestorUser(investorUserId, displayName) {
  if (!investorUserId) return;
  const normalized =
    displayName != null && String(displayName).trim() !== '' ? String(displayName).trim() : null;
  const q = query(collection(firestore, COLLECTION), where('investorUserId', '==', investorUserId));
  const snap = await getDocs(q);
  if (snap.empty) return;
  await Promise.all(
    snap.docs.map((d) =>
      updateDoc(doc(firestore, COLLECTION, d.id), {
        displayName: normalized,
        updatedAt: serverTimestamp(),
      }),
    ),
  );
}

export async function linkFundInvestorToUser(fundInvestorId, investorUserId) {
  if (!fundInvestorId || !investorUserId) return;
  const uid = auth.currentUser?.uid;
  if (uid && uid !== investorUserId) {
    const err = new Error('INVITATION_EMAIL_MISMATCH');
    err.code = 'INVITATION_EMAIL_MISMATCH';
    throw err;
  }
  const ref = doc(firestore, COLLECTION, fundInvestorId);
  const snap = await getDoc(ref);
  if (!snap.exists()) {
    const err = new Error('Placement not found');
    err.code = 'not-found';
    throw err;
  }
  const prev = snap.data() || {};
  const inviteEmail = String(prev.email || '').trim().toLowerCase();
  const userEmail = String(auth.currentUser?.email || '').trim().toLowerCase();
  if (inviteEmail && userEmail && inviteEmail !== userEmail) {
    const err = new Error('INVITATION_EMAIL_MISMATCH');
    err.code = 'INVITATION_EMAIL_MISMATCH';
    throw err;
  }
  const patch = {
    investorUserId,
    updatedAt: serverTimestamp(),
  };
  if (prev.status === 'invited') {
    patch.status = 'invitation_accepted';
  }
  const authDisplay =
    auth.currentUser?.displayName && String(auth.currentUser.displayName).trim()
      ? String(auth.currentUser.displayName).trim()
      : '';
  if (authDisplay) {
    patch.displayName = authDisplay;
  }
  await updateDoc(ref, patch);
}

/**
 * Investor: self-attestation as qualified (accredited / eligible) under applicable Canadian securities law.
 * Optional email match: account email must match invitation email when both are present.
 *
 * @param {string} fundInvestorId
 * @param {{ category: 'accredited' | 'eligible' | 'institution', confirmNotAdvice: boolean, confirmQualification: boolean }} payload
 */
export async function submitQualifiedInvestorAttestation(fundInvestorId, payload) {
  if (!fundInvestorId) throw new Error('fundInvestorId required');
  const uid = auth.currentUser?.uid;
  if (!uid) throw new Error('Not authenticated');
  const { category, confirmNotAdvice, confirmQualification } = payload || {};
  if (!confirmNotAdvice || !confirmQualification) throw new Error('Confirmations required');
  const allowed = ['accredited', 'eligible', 'institution'];
  if (!allowed.includes(category)) throw new Error('Invalid category');

  const ref = doc(firestore, COLLECTION, fundInvestorId);
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error('Placement not found');
  const data = snap.data();
  if (data.investorUserId !== uid) throw new Error('Not your placement');
  if (data.qualifiedInvestorAttestedAt != null) throw new Error('Already attested');

  if (data.investorInvestingHolderCommittedAt == null) {
    const err = new Error('HOLDER_NOT_COMMITTED');
    err.code = 'HOLDER_NOT_COMMITTED';
    throw err;
  }

  const serverEntity = data.investingEntityType === 'company' ? 'company' : 'individual';
  if (serverEntity === 'company') {
    const cid = data.investingCompanyId;
    if (cid == null || String(cid).trim() === '') {
      const err = new Error('HOLDER_COMPANY_REQUIRED');
      err.code = 'HOLDER_COMPANY_REQUIRED';
      throw err;
    }
  }

  const userEmail = auth.currentUser?.email?.trim().toLowerCase() || '';
  const inviteEmail = (data.email || '').trim().toLowerCase();
  if (userEmail && inviteEmail && userEmail !== inviteEmail) {
    const err = new Error('EMAIL_MISMATCH');
    err.code = 'EMAIL_MISMATCH';
    throw err;
  }

  await updateDoc(ref, {
    qualifiedInvestorCategory: category,
    qualifiedInvestorAttestationVersion: QUALIFIED_INVESTOR_ATTESTATION_VERSION,
    qualifiedInvestorAttestedAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
}

/**
 * @param {string} id
 * @returns {Promise<object | null>}
 */
export async function getFundInvestor(id) {
  const d = await getDoc(doc(firestore, COLLECTION, id));
  if (!d.exists()) return null;
  return { ...d.data(), id: d.id };
}

/**
 * @param {string} lenderUid
 * @param {(items: Array<{ id: string } & object>) => void} callback
 * @returns {() => void} unsubscribe
 */
export function subscribeFundInvestorsByLender(lenderUid, callback) {
  if (!lenderUid) {
    callback([]);
    return () => {};
  }
  const q = query(
    collection(firestore, COLLECTION),
    where('lenderUid', '==', lenderUid),
    orderBy('createdAt', 'desc')
  );
  return onSnapshot(
    q,
    (snap) => {
      const items = snap.docs.map((d) => ({ ...d.data(), id: d.id }));
      callback(items);
    },
    (err) => {
      console.error('[fundInvestors] snapshot error (by lender):', err?.code, err?.message);
      callback([]);
    }
  );
}

/**
 * Subscribe to fundInvestors where the current user is the linked investor.
 * @param {string} investorUserId
 * @param {(items: Array<{ id: string } & object>) => void} callback
 * @returns {() => void} unsubscribe
 */
export function subscribeFundInvestorsByInvestor(investorUserId, callback) {
  if (!investorUserId) {
    callback([]);
    return () => {};
  }
  const q = query(
    collection(firestore, COLLECTION),
    where('investorUserId', '==', investorUserId),
    orderBy('createdAt', 'desc')
  );
  return onSnapshot(q, (snap) => {
    const items = snap.docs.map((d) => ({ ...d.data(), id: d.id }));
    callback(items);
  });
}

/**
 * @param {string} id
 * @param {object} data - fields to update (e.g. status, contractSignedAt, investorUserId)
 * @returns {Promise<void>}
 */
export async function updateFundInvestor(id, data) {
  const ref = doc(firestore, COLLECTION, id);
  await updateDoc(ref, {
    ...data,
    updatedAt: serverTimestamp(),
  });
}

/**
 * Investor: request AI summary of the fund placement contract (FR+EN). Cached on fundInvestor doc.
 * @param {string} fundInvestorId
 * @returns {Promise<{ summaryFr: string, summaryEn: string }>}
 */
export async function summarizeFundInvestorContract(fundInvestorId) {
  if (!fundInvestorId) throw new Error('fundInvestorId required');
  const { data } = await summarizeFundInvestorContractCallable({ fundInvestorId });
  return {
    summaryFr: data?.summaryFr ?? '',
    summaryEn: data?.summaryEn ?? '',
  };
}
